import { useState } from 'react';
import MediaPreview from '@/components/base/MediaPreview';
import type { SectionContent, FileItem } from '@/mocks/achievement-detail-sections';

interface DetailContentProps {
  content: SectionContent | null;
  defaultContent?: React.ReactNode;
}

interface PreviewState {
  type: 'pdf' | 'video' | 'image';
  url: string;
  title: string;
}

// ── PDF file preview using real iframe ──
function PdfPreview({ url, title }: { url: string; title: string }) {
  return (
    <div className="rounded-lg overflow-hidden bg-white border border-background-200" style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.03)' }}>
      {/* PDF iframe */}
      <div className="relative bg-background-100" style={{ minHeight: '500px' }}>
        {url !== '#' ? (
          <iframe
            src={url}
            title={title}
            className="w-full h-[500px] sm:h-[600px] md:h-[800px] border-0"
            style={{ display: 'block' }}
          />
        ) : (
          <div className="flex items-center justify-center h-[500px] sm:h-[600px] md:h-[800px] text-center text-foreground-400">
            <div>
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 rounded-xl bg-background-200 text-foreground-300">
                <i className="ri-file-pdf-line text-2xl" />
              </div>
              <p className="text-sm font-medium text-foreground-600">PDF 文档</p>
              <p className="text-xs text-foreground-400 mt-1">暂无在线预览文件</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inline file content display box ──
function FileContentView({ title, file }: { title: string; file: FileItem }) {
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const handleOpen = () => {
    if (['pdf', 'image', 'video'].includes(file.fileType) && file.url !== '#') {
      setPreview({
        type: file.fileType as PreviewState['type'],
        url: file.url,
        title: file.name,
      });
    }
  };

  const handleDownload = () => {
    if (file.url && file.url !== '#') {
      const a = document.createElement('a');
      a.href = file.url;
      a.download = '';
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const iconMap: Record<string, string> = {
    pdf: 'ri-file-pdf-line',
    image: 'ri-image-line',
    video: 'ri-video-line',
    file: 'ri-file-line',
  };

  const typeLabelMap: Record<string, string> = {
    pdf: 'PDF 文档',
    image: '图片文件',
    video: '视频文件',
    file: '文档文件',
  };

  const fileIcon = iconMap[file.fileType] || iconMap.file;
  const typeLabel = typeLabelMap[file.fileType] || typeLabelMap.file;

  return (
    <div>
      {/* Section Title — skip for video type */}
      {file.fileType !== 'video' && (
        <h2 className="text-[18px] sm:text-[20px] md:text-[22px] font-bold font-heading text-foreground-800 mb-3">{title}</h2>
      )}
      {/* ── 动车车头样式装饰 ── */}
      {file.fileType !== 'video' && (
        <div className="relative mb-6" style={{ height: '10px' }}>
          {/* 车身装饰线 */}
          <span
            className="absolute bottom-0 left-0 block bg-gradient-to-r from-primary-600 to-primary-500"
            style={{
              width: '100px',
              height: '3px',
              borderRadius: '2px',
            }}
          />
          {/* 车头流线型尖端 */}
          <span
            className="absolute bottom-0 left-[100px] block bg-primary-500"
            style={{
              width: '50px',
              height: '3px',
              borderRadius: '2px',
              clipPath: 'polygon(0 0, 100% 40%, 100% 60%, 0 100%)',
            }}
          />
          {/* 车窗高光线 */}
          <span
            className="absolute top-0 left-[20px] block bg-primary-400/40"
            style={{
              width: '60px',
              height: '2px',
              borderRadius: '1px',
            }}
          />
          {/* 底部轨道线 */}
          <span
            className="absolute -bottom-[3px] left-0 block bg-background-300"
            style={{
              width: '140px',
              height: '1px',
            }}
          />
        </div>
      )}

      {/* ── Content Preview ── */}
      {file.fileType === 'pdf' && (
        <PdfPreview url={file.url} title={file.name} />
      )}

      {file.fileType === 'image' && (
        <div className="rounded-lg overflow-hidden bg-white border border-background-200" style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.03)' }}>
          <div className="flex items-center gap-3 px-5 py-3 border-b border-background-200 bg-background-50">
            <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-500 text-white shrink-0">
              <i className={`${fileIcon} text-base`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground-800 truncate">{file.name}</p>
              <p className="text-[11px] text-foreground-400">{typeLabel}</p>
            </div>
            <button
              onClick={handleDownload}
              className="text-xs font-medium text-foreground-600 hover:text-foreground-800 px-4 py-1.5 rounded-md border border-background-300 hover:border-background-400 hover:bg-background-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="flex items-center gap-1.5">
                <i className="ri-download-2-line" />
                下载
              </span>
            </button>
          </div>
          <div className="relative w-full" style={{ aspectRatio: '4/3' }}>
            {file.url !== '#' ? (
              <img
                src={file.url}
                alt={file.name}
                className="w-full h-full object-contain bg-white"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-foreground-400 bg-background-100">
                <div className="text-center">
                  <i className="ri-image-line text-4xl text-foreground-300 mb-2" />
                  <p className="text-sm">图片文件</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {file.fileType === 'video' && (
        <div className="rounded-lg overflow-hidden bg-white border border-background-200" style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.03)' }}>
          <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
            {file.url !== '#' ? (
              <video
                src={file.url}
                controls
                className="w-full h-full"
                style={{ display: 'block' }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-foreground-400">
                <div className="text-center">
                  <i className="ri-video-line text-4xl text-foreground-300 mb-2" />
                  <p className="text-sm">视频文件</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {file.fileType === 'file' && (
        <div className="rounded-lg overflow-hidden bg-white border border-background-200" style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.03)' }}>
          <div className="flex items-center gap-3 px-5 py-3 border-b border-background-200 bg-background-50">
            <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-500 text-white shrink-0">
              <i className={`${fileIcon} text-base`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground-800 truncate">{file.name}</p>
              <p className="text-[11px] text-foreground-400">{typeLabel}</p>
            </div>
            <button
              onClick={handleDownload}
              className="text-xs font-medium text-foreground-600 hover:text-foreground-800 px-4 py-1.5 rounded-md border border-background-300 hover:border-background-400 hover:bg-background-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="flex items-center gap-1.5">
                <i className="ri-download-2-line" />
                下载
              </span>
            </button>
          </div>
          <div className="p-10 bg-background-50 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-xl bg-background-100 text-foreground-300">
              <i className="ri-file-3-line text-3xl" />
            </div>
            <p className="text-sm font-medium text-foreground-700 mb-1">{file.name}</p>
            <p className="text-xs text-foreground-400 mb-5">文档文件</p>
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-500 hover:text-primary-600 px-4 py-1.5 rounded border border-primary-200 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-2-line" />
              下载文件
            </button>
          </div>
        </div>
      )}

      {preview && (
        <MediaPreview
          type={preview.type}
          url={preview.url}
          title={preview.title}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

function TextView({ title, content }: { title: string; content: string }) {
  return (
    <div>
      {/* Section Header */}
      <h2
        className="text-[22px] sm:text-[26px] md:text-[28px] font-semibold font-heading text-foreground-900"
        style={{ letterSpacing: '0.05em' }}
      >
        {title}
      </h2>
      <div className="flex items-center gap-2 mt-3 mb-6 md:mb-8">
        <span className="block h-[3px] rounded-full bg-primary-500" style={{ width: '48px' }} />
        <span className="block h-[3px] rounded-full bg-primary-500/30" style={{ width: '14px' }} />
      </div>

      {/* Text Content */}
      <div className="bg-white rounded-lg p-5 sm:p-6 md:p-10 rich-text-content"
        style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.03)' }}>
        <div
          className="prose prose-foreground max-w-none"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    </div>
  );
}

export default function DetailContent({ content, defaultContent }: DetailContentProps) {
  if (!content) {
    return (
      <div className="flex-1 min-h-[60vh] flex items-center justify-center">
        {defaultContent || (
          <div className="text-center text-foreground-400">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4">
              <i className="ri-arrow-left-circle-line text-4xl" />
            </div>
            <p className="text-sm">请从左侧目录选择内容查看</p>
          </div>
        )}
      </div>
    );
  }

  if (content.contentType === 'text') {
    return (
      <div className="flex-1">
        <TextView title={content.title} content={content.content} />
      </div>
    );
  }

  return (
    <div className="flex-1">
      <FileContentView title={content.title} file={content.files[0]} />
    </div>
  );
}